import { isPastShift as isPastShiftFn } from '../../../../backend/utils/shiftStateUtils';
import { useParticipantsByShiftId } from '../../../../ui/features/shift/hooks/useParticipantsByShiftId';
import { useShift } from '../../../../ui/features/shift/hooks/useShift';
import { useMissionsTranslation } from '../../../../ui/hooks';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PermContactCalendarOutlined from '@mui/icons-material/PermContactCalendarOutlined';
import { Alert, Box, Divider, FormControl, Grid, InputLabel, List, ListItem, Stack, Typography } from '@mui/material';
import React from 'react';
import { PositionSelect } from '../../shift-details/components/PositionSelect';
export const SelectPositionStep = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e;
    const { t } = useMissionsTranslation();
    const shiftId = props.data.shift.id;
    const { shift } = useShift(shiftId);
    const { participants } = useParticipantsByShiftId({ shiftId });
    const isPastShift = (shift === null || shift === void 0 ? void 0 : shift.data) ? isPastShiftFn(shift.data) : false;
    const allShiftPositions = (_b = (_a = shift === null || shift === void 0 ? void 0 : shift.data.shiftPositions) === null || _a === void 0 ? void 0 : _a.map(({ id, data, quantity }) => {
        var _a;
        return ({
            id,
            name: data.name,
            approvedParticipants: (_a = participants === null || participants === void 0 ? void 0 : participants.rows.filter(({ columns }) => columns.status === 'approved' && columns.positionId === id).map(({ columns }) => ({ id: columns.participantId }))) !== null && _a !== void 0 ? _a : [],
            total: quantity,
        });
    })) !== null && _b !== void 0 ? _b : [];
    const unfilledShiftPositions = allShiftPositions.filter(({ approvedParticipants, total }) => approvedParticipants.length < total);
    const allShiftPositionsFilled = unfilledShiftPositions.length === 0;
    const visibleShiftPositions = isPastShift ? unfilledShiftPositions : allShiftPositions;
    (_c = props.disableSubmit) === null || _c === void 0 ? void 0 : _c.call(props, allShiftPositionsFilled);
    const [selectedPositionId, setSelectedPositionId] = React.useState(unfilledShiftPositions.find(({ id }) => { var _a, _b; return ((_b = (_a = props.data) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.id) === id; }) ? (_e = (_d = props.data) === null || _d === void 0 ? void 0 : _d.position) === null || _e === void 0 ? void 0 : _e.id : undefined);
    const handlePositionChange = (positionId) => {
        const newPosition = visibleShiftPositions.find((position) => position.id === positionId);
        if (newPosition) {
            setSelectedPositionId(newPosition.id);
        }
    };
    const positionsFilledCount = allShiftPositions.reduce((positionsFilledCount, position) => position.approvedParticipants.length === position.total ? positionsFilledCount + 1 : positionsFilledCount, 0);
    const volunteersNeededCount = allShiftPositions.reduce((volunteersNeededCount, position) => volunteersNeededCount + position.total - position.approvedParticipants.length, 0);
    const totalVolunteersCount = allShiftPositions.reduce((totalVolunteersCount, position) => totalVolunteersCount + position.total, 0);
    React.useImperativeHandle(ref, () => ({
        getData: () => ({ position: { id: selectedPositionId } }),
        submit: () => {
            if (selectedPositionId) {
                return props
                    .onSubmit({ position: { id: selectedPositionId } })
                    .then((response) => props.onSubmitSuccess({ position: { id: selectedPositionId } }, response));
            }
            else {
                return Promise.reject(t('shiftVolunteerManagementWizard.validation.positionNotSelected'));
            }
        },
    }));
    const selectPositionMenuItems = visibleShiftPositions.map(({ id, name }) => ({
        positionId: id,
        positionName: name,
    }));
    return (React.createElement(Stack, { spacing: 2, my: 2 },
        React.createElement(Stack, { borderRadius: 0.5, component: 'section', p: 2, spacing: 2, bgcolor: themeColors.surface.secondary },
            React.createElement(Typography, { variant: 'subtitle1' }, t('shiftVolunteerManagementWizard.titles.shiftSummary')),
            React.createElement(Box, null,
                React.createElement(Grid, { container: true, spacing: 2 },
                    React.createElement(Grid, { item: true, sm: 6, xs: 12 },
                        React.createElement(Alert, { sx: { height: '100%' }, severity: 'info', icon: React.createElement(OutlinedFlagIcon, null) }, t('shiftVolunteerManagementWizard.alerts.filled', {
                            count: allShiftPositions.length,
                            totalCount: allShiftPositions.length,
                            filledCount: positionsFilledCount,
                        }))),
                    React.createElement(Grid, { item: true, sm: 6, xs: 12 },
                        React.createElement(Alert, { sx: { height: '100%' }, severity: 'info', icon: React.createElement(PermContactCalendarOutlined, null) }, t('shiftVolunteerManagementWizard.alerts.needed', {
                            count: totalVolunteersCount,
                            totalCount: totalVolunteersCount,
                            neededCount: volunteersNeededCount,
                        }))))),
            React.createElement(Box, { sx: {
                    backgroundColor: themeColors.decorative.white,
                    borderRadius: 0.5,
                    border: 1,
                    borderColor: themeColors.border.main,
                    maxHeight: 290,
                    overflowY: 'auto',
                } },
                React.createElement(List, { sx: { p: 0 } }, allShiftPositions.map((position, index, arr) => (React.createElement(React.Fragment, { key: position.id },
                    React.createElement(ListItem, { sx: { py: 2.3 }, key: position.id },
                        React.createElement(Grid, { container: true, spacing: 0.5, alignItems: 'center' },
                            React.createElement(Grid, { item: true, sm: 6, xs: 12 },
                                React.createElement(Stack, { justifyContent: 'space-between', direction: 'row' },
                                    React.createElement(Typography, { variant: 'body2' }, position.name),
                                    position.approvedParticipants.length === position.total && (React.createElement(CheckCircleIcon, { htmlColor: themeColors.accent.green.base, fontSize: 'small' })))),
                            React.createElement(Grid, { item: true, sm: 6, xs: 12 },
                                React.createElement(Typography, { variant: 'subtitle2' },
                                    position.approvedParticipants.length,
                                    "/",
                                    position.total)))),
                    index < arr.length - 1 && (React.createElement(Box, { px: 2 },
                        React.createElement(Divider, null))))))))),
        React.createElement(Stack, { borderRadius: '4px', component: 'section', p: 2, bgcolor: themeColors.surface.secondary },
            React.createElement(Grid, { container: true, spacing: 2, alignItems: 'center' },
                React.createElement(Grid, { item: true, sm: 6, xs: 12 },
                    React.createElement(Typography, { variant: 'subtitle1' }, t('shiftVolunteerManagementWizard.titles.selectPositionForVolunteers'))),
                React.createElement(Grid, { item: true, sm: 6, xs: 12 },
                    React.createElement(FormControl, { fullWidth: true },
                        React.createElement(InputLabel, { id: 'shift-position' }, t('shiftVolunteerManagementWizard.labels.shiftPosition')),
                        React.createElement(PositionSelect, { fullWidth: true, label: t('shiftVolunteerManagementWizard.labels.shiftPosition'), labelId: 'shift-position', onSelectPosition: handlePositionChange, positions: selectPositionMenuItems, selectedPosition: selectedPositionId, disabled: isPastShift && allShiftPositionsFilled }))))),
        isPastShift && allShiftPositionsFilled && (React.createElement(Alert, { severity: 'warning' },
            React.createElement(Typography, { variant: 'subtitle2' }, t('shiftVolunteerManagementWizard.alerts.allPositionsFull'))))));
});
